<template>
  <div class="shares-calculator-wrapper">

    <div class="filters-wrapper">
      <div class="shares-info">
        Share type: <b>{{ getShareTypeUI }}</b>
      </div>

      <div class="filters">
        <datepickers @onDateChange="onDateChange"  @onInit="onDatepickerInit"/>

        <b-row>
          <b-col cols="12" sm="4">
            <b-form-group
                    label="Enter number of shares"
                    label-for="input-1">
              <b-form-input v-model="inputs.numberOfShares" id="input-1" :disabled="isNumberOfSharesInputLocked" v-on:keyup.enter="compute" autocomplete="off"></b-form-input>
            </b-form-group>
          </b-col>

          <b-col cols="12" sm="4">
            <b-form-group
                    label="Enter holding value"
                    label-for="input-2">
              <b-form-input v-model="inputs.finalInvestment" id="input-2" :disabled="isValueInputLocked" v-on:keyup.enter="compute" autocomplete="off"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <app-button-secondary text="Compute" v-on:click.native="compute" :isLoading="isLoading"></app-button-secondary>
      </div>
    </div>

    <div class="calculator-results-wrapper" v-if="isCalculationsElementVisible">
      <b-card-group deck>
        <b-card
                border-variant="primary"
                header="Initial value of the investment"
                header-bg-variant="primary"
                align="center">

          <b-card-text>
            <p>
              (End of the day)
            </p>

            <b-table id="initial-table" striped hover :items="initialInvestment.table.items" :fields="initialInvestment.table.fields" show-empty>
              <template slot="empty">
                <div v-if="isCalculatedValuesLoading">
                  <table-loader/>
                </div>

                <div v-else>Unfortunately no data available.</div>
              </template>
            </b-table>

          </b-card-text>
        </b-card>

        <b-card
                border-variant="primary"
                header="Final value of the investment"
                header-bg-variant="primary"
                align="center">

          <b-card-text>
            <p>
              ({{ getFinalDayType }})
            </p>

            <b-table id="holding-table" striped hover :items="finalInvestment.table.items" :fields="finalInvestment.table.fields" show-empty>
              <template slot="empty">
                <div v-if="isCalculatedValuesLoading">
                  <table-loader/>
                </div>

                <div v-else>Unfortunately no data available.</div>
              </template>
            </b-table>
          </b-card-text>
        </b-card>
      </b-card-group>
    </div>

    <div class="history-wrapper" v-if="isHistoryVisible">
      <h3>History</h3>

      <p>* Part of the day</p>

      <div class="history-table-wrapper">
        <b-table id="history-table" striped hover :items="history.table.items" :fields="history.table.fields" show-empty>
          <template slot="empty">
            <div v-if="isAllLoading">
              <table-loader/>
            </div>

            <div v-else>Unfortunately no data available.</div>
          </template>
        </b-table>
      </div>
    </div>

  </div>
</template>

<script>
  // Api
  import ShareCalculatorApi from "@/services/share-calculator";

  // Components
  import Datepickers from '@/views/investor/shares/children/calculator/components/Datepickers';
  import AppButtonSecondary from '@/components/ui/buttons/AppButtonPrimary';
  import TableLoader from '@/components/ui/loaders/TableLoader';

  // Mixins
  import {mixinDatesHandler} from '@/mixins/dates-handler';
  import {mixinAmountsHandler} from '@/mixins/amounts-handler';

  // Configs
  import DatesConfig from "@/configs/dates";

  // Model
  import ShareModel from "@/models/share";

  const UI_MOMENT_COMPARE_DATE_FORMAT = DatesConfig.UI_MOMENT_COMPARE_DATE_FORMAT;
  const API_REQUEST_DATE_FORMAT       = DatesConfig.API_REQUEST_DATE_FORMAT;

  export default {
    name: 'ShareCalculator',

    mixins: [mixinDatesHandler, mixinAmountsHandler],

    components: {
      Datepickers, AppButtonSecondary, TableLoader
    },

    data() {
      return {
        isLoading: false,

        history: {
          table: {
            fields: [
              {
                key: 'date',
                label: 'Date',
                sortable: false
              },
              {
                key: 'openPrice',
                label: 'Open price',
                sortable: false
              },
              {
                key: 'ask',
                label: 'Ask',
                sortable: false
              },
              {
                key: 'bid',
                label: 'Bid',
                sortable: false
              },
              {
                key: 'price',
                label: 'Price',
                sortable: false
              },
              {
                key: 'high',
                label: 'High',
                sortable: false
              },
              {
                key: 'low',
                label: 'Low',
                sortable: false
              },
              {
                key: 'volume',
                label: 'Volume',
                sortable: false
              },
              {
                key: 'turnover',
                label: 'Turnover',
                sortable: false
              },
            ],
            items: [],
          },
        },

        initialInvestment: {
          table: {
            fields: [
              {
                key: 'label',
                sortable: false
              },
              {
                key: 'value',
                sortable: false
              },
            ],
            items: [],
          },
        },

        finalInvestment: {
          quoteType: null,
          table: {
            fields: [
              {
                key: 'label',
                sortable: false
              },
              {
                key: 'value',
                sortable: false
              },
            ],
            items: [],
          },
        },

        inputs: {
          numberOfShares: null,
          finalInvestment: null,
        },

        filter:{
          startDate: null,
          endDate: null,
        },

        states: {
          firstDataFetched: false,
          isCalculationsElementVisible: false,

          datesChanged: true,
        },

        settings: {},

        returnData: [],
      }
    },

    methods: {
      startLoader() {
        this.isLoading = true;
      },

      stopLoader() {
        this.isLoading = false;
      },

      onDatepickerInit(obj) {
        this.filter.startDate = obj.startDate;
        this.filter.endDate   = obj.endDate;
      },

      onDateChange(obj) {
        this.filter.startDate = obj.startDate;
        this.filter.endDate   = obj.endDate;

        this.states.datesChanged = true;
      },

      fetchData() {
        return new Promise((resolve) => {
          let data = {
            params: {
              startDate: this.startDateApiRequestValue,
              endDate: this.endDateApiRequestValue,
              tickerSymbol: this.getShareType
            }
          }

          new ShareCalculatorApi(data, {}).get(function (response) {
            if (response.status !== 200) {
              return resolve([]);
            }

            return resolve(response.data);
          });
        });
      },

      setHistoryItems() {
        const instrument = this.returnData.Instruments[0];
        const quotes     = instrument.Quotes;
        let items        = [];

        quotes.forEach((x) => {

          let date = this._mixinDateHandler_ApiResponseDateTimeToFormat(x.QuoteTime, 'DD MMM YYYY', true);
          if (x.QuoteType === 'PartOfDay') {
            date = `${date} *`;
          }

          items.push({
            date: date,
            openPrice: this.formatAmount(x.OpenPrice, 2),
            ask: this.formatAmount(x.AskPrice, 2),
            bid: this.formatAmount(x.BidPrice, 2),
            price: this.formatAmount(x.Price, 2),
            high: this.formatAmount(x.HighPrice, 2),
            low: this.formatAmount(x.LowPrice, 2),
            volume: this.formatAmount(x.Quantity, 0),
            turnover: this.formatAmount(x.Turnover, 0),
          });
        });

        this.history.table.items = items.reverse();
      },

      formatAmount(amount, decimals) {
        return this._mixinAmountsHandler_formatAmount(amount, decimals);
      },

      setInitialInvestment() {
        const instrument = this.returnData.Instruments[0];
        const currency   = instrument.TradeCurrency;
        const startQuote = instrument.StartDateQuote;

        let items = [];

        // Number of shares
        let numberOfShares = this.inputs.numberOfShares;
        const finalInvestment = this.inputs.finalInvestment;

        if (!numberOfShares) {
          numberOfShares = parseInt(parseFloat(finalInvestment) / startQuote.Price);
        }

        items.push({
          label: 'Date',
          value: this._mixinDateHandler_ApiResponseDateTimeToFormat(startQuote.QuoteTime, 'DD MMM YYYY', true)
        });

        items.push({
          label:`Value (${currency})`,
          value: this.formatAmount(numberOfShares * startQuote.Price, 2)
        });

        items.push({
          label:`Share price (${currency})`,
          value: this.formatAmount(startQuote.Price, 2)
        });

        items.push({
          label: 'Shares bought',
          value: this.formatAmount(numberOfShares, 0)
        });

        this.initialInvestment.table.items = items;
      },

      setFinalValue() {
        const instrument = this.returnData.Instruments[0];
        const currency   = instrument.TradeCurrency;
        const startQuote = instrument.StartDateQuote;
        const endQuote   = instrument.EndDateQuote;

        let items = [];

        // Number of shares
        let numberOfShares    = this.inputs.numberOfShares;
        const finalInvestment = this.inputs.finalInvestment;

        if (!numberOfShares) {
          numberOfShares = parseInt(parseFloat(finalInvestment) / startQuote.Price);
        }

        const startValue = numberOfShares * startQuote.Price;
        const endValue   = numberOfShares * endQuote.Price;

        // Change value
        const changeValue = endValue - startValue;

        // Change percentage
        const changePercent = (changeValue / startValue) * 100;

        // Annualized rate
        // Annualized Rate Formula: RA = (((((SV + CV) / SV) ^(1 / (n / 365))) - 1) * 100)

        // RA => Annualized rate of return (Result)
        // n  => Number of years held
        // SV => Start value
        // CV => Change value

        // Base     => (SV + CV) / SV)
        // Exponent => (1 / (n / 365))

        const base                    = ((startValue + changeValue) / startValue); // Can be simplified to be (endValue / startValue)
        const diffDays                = this.$moment(this.filter.endDate).diff(this.$moment(this.filter.startDate), 'days');
        const exponent                = (1 / (diffDays / 365)); // Can be simplified to (365 / diffDays)
        const changeAnnualizedPercent = ((Math.pow(base, exponent) - 1) * 100);

        items.push({
          label: 'Date',
          value: this._mixinDateHandler_ApiResponseDateTimeToFormat(endQuote.QuoteTime, 'DD MMM YYYY', true)
        });

        items.push({
          label:`Value (${currency})`,
          value: this.formatAmount(numberOfShares * endQuote.Price, 2)
        });

        items.push({
          label:`Share price (${currency})`,
          value: this.formatAmount(endQuote.Price, 2)
        });

        items.push({
          label:`Change (${currency})`,
          value: this.formatAmount(changeValue, 2)
        });

        items.push({
          label:'Change (%)',
          value: this.formatAmount(changePercent, 2)
        });

        items.push({
          label: 'Annualized change (%)',
          value: this.formatAmount(changeAnnualizedPercent, 2)
        });

        this.finalInvestment.quoteType   = endQuote.QuoteType;
        this.finalInvestment.table.items = items;
      },

      setResponseData() {
        if ('Instruments' in this.returnData && this.returnData.Instruments.length > 0) {
          if ('EndDateQuote' in this.returnData.Instruments[0] && 'StartDateQuote' in this.returnData.Instruments[0]) {
            // Initial Investment
            this.setInitialInvestment();

            // Final investment
            this.setFinalValue();

            // Toggle Calculations element
            this.toggleCalculationsElement();
          }

          // History
          this.setHistoryItems();
        }

        this.stopLoader();
      },

      async fetchDataAsync() {
        //const data = await this.fetchData();

        this.returnData = await this.fetchData();

        if (!this.states.firstDataFetched) {
          this.states.firstDataFetched = true;
        }

        this.setResponseData();
      },

      compute() {
        if (!this.isLoading) {
          this.startLoader();
          this.clearCalculatedData();

          if (this.states.datesChanged) {
            this.clearHistoryData();

            this.fetchDataAsync();

            // Reset date state
            this.states.datesChanged = false;

            return;
          }

          this.setResponseData();
        }
      },

      toggleCalculationsElement() {
        const sharesInput = this.inputs.numberOfShares;
        const valueInput  = this.inputs.finalInvestment;
        const data        = this.returnData;

        let state = false;
        if ((sharesInput || valueInput) && data) {
          state = true;
        }

        this.states.isCalculationsElementVisible = state;
      },

      clearNumberOfSharesInput() {
        this.inputs.numberOfShares = null;
      },

      clearValueInput() {
        this.inputs.finalInvestment = null;
      },

      clearHistoryData() {
        this.history.table.items = [];
      },

      clearCalculatedData() {
        this.initialInvestment.table.items = [];
        this.finalInvestment.table.items   = [];
      }
    },

    computed: {
      getFinalDayType() {
        return this.finalInvestment.quoteType === 'PartOfDay' ? 'Part of the day' : 'End of the day';
      },

      isCalculationsElementVisible() {
        return this.states.isCalculationsElementVisible;
      },

      isHistoryVisible() {
        return this.states.firstDataFetched;
      },

      isValueInputLocked() {
        return !!this.inputs.numberOfShares;
      },

      isNumberOfSharesInputLocked() {
        return !!this.inputs.finalInvestment;
      },

      isAllLoading() {
        return this.isLoading &&
            this.history.table.items.length === 0 &&
            this.initialInvestment.table.items.length === 0 &&
            this.finalInvestment.table.items.length === 0;
      },

      isCalculatedValuesLoading() {
        return (this.isLoading &&
               this.initialInvestment.table.items.length === 0 &&
               this.finalInvestment.table.items.length === 0);
      },

      startDateApiRequestValue() {
        return this.$moment(this.filter.startDate, UI_MOMENT_COMPARE_DATE_FORMAT).format(API_REQUEST_DATE_FORMAT);
      },

      endDateApiRequestValue() {
        return this.$moment(this.filter.endDate, UI_MOMENT_COMPARE_DATE_FORMAT).format(API_REQUEST_DATE_FORMAT);
      },

      getShareTypeUI() {
        return `${this.settings.instruments.main[0].symbol} (${this.settings.instruments.main[0].currency})`;
      },

      getShareType() {
        return this.settings.instruments.main[0].symbol;
      },
    },

    created() {
      const model = new ShareModel();

      if (process.env.VUE_APP_IS_PRODUCTION === 'true') {
        this.settings = model.getProdSettings();
      } else {
        this.settings = model.getDevSettings();
      }
    },

    watch: {
      'inputs.numberOfShares': function () {
        this.clearValueInput();
      },

      'inputs.finalInvestment': function () {
        this.clearNumberOfSharesInput();
      },
    }
  }
</script>

<style scoped lang="scss">
.shares-calculator-wrapper {
  width: 100%;

  .filters-wrapper {
    width: 100%;

    .shares-info {
      margin-bottom: 30px;
    }
  }

  .calculator-results-wrapper {
    width: 100%;
    margin-bottom: 30px;
  }

  ::v-deep#initial-table, ::v-deep#holding-table {
    thead {
      display: none;
    }

    tr:nth-child(1):not(.b-table-empty-row ) td {
      font-weight: bold;
    }

    tr:not(.b-table-empty-row ) td:nth-child(1) {
      text-align: left;
    }

    tr:not(.b-table-empty-row ) td:nth-child(2) {
      text-align: right;
    }

    .b-table-empty-row {
      text-align: center;
    }
  }

  ::v-deep.border-primary {
    border-color: #dedede !important;

    .bg-primary {
      background-color: #dedede !important;
    }
  }

  .history-table-wrapper {
    width: 100%;
    overflow-y: hidden;
    overflow-x: auto;

/*    -ms-overflow-style: none;  !* IE and Edge *!
    scrollbar-width: none;  !* Firefox *!

    !* Hide scrollbar for Chrome, Safari and Opera *!
    &::-webkit-scrollbar {
      display: none;
    }*/
  }

  ::v-deep#history-table {
    thead {
      font-weight: bold;

      tr th:not(:nth-child(1)) {
        text-align: right;
      }
    }

    tr td:first-child {
      min-width: 165px;
    }

    tr td:nth-child(2) {
      min-width: 113px;
    }

    tr td:not(:nth-child(1)) {
      text-align: right;
    }

    .b-table-empty-row {
      text-align: center;
    }
  }
}
</style>
